import { createMission } from "../api.ts";
import { t } from "../i18n/index.ts";
import type { QuestDetail } from "./types.ts";
import { button, el, questBar, questViewWorkspace } from "./dom.ts";

const ROUTE_ART = `<svg viewBox="0 0 160 70" aria-hidden="true"><path d="M50 52c6-14 0-22 10-30 8-6 14 4 22-6 6-8 18-6 20 2 3 10-8 12-2 22 5 8 16 6 14 18-2 9-14 6-20 0-6-6-10 2-18 0-7-2-4-10-12-10-6 0-10 8-14 4z" fill="none" stroke="#8b5cf6" stroke-width="3" stroke-linejoin="round"/><circle cx="50" cy="52" r="6" fill="#1f9d55"/><text x="50" y="55" text-anchor="middle" font-size="7" font-weight="800" fill="#fff">S</text><circle cx="96" cy="38" r="6" fill="#111"/><text x="96" y="41" text-anchor="middle" font-size="7" font-weight="800" fill="#fff">F</text></svg>`;
const SPOT_ART = `<svg viewBox="0 0 160 70" aria-hidden="true"><path d="M28 46 L62 26 L100 46 L134 26" fill="none" stroke="#8b5cf6" stroke-width="2.5" stroke-dasharray="5 5"/><circle cx="28" cy="46" r="8" fill="#1f9d55"/><text x="28" y="49" text-anchor="middle" font-size="8" font-weight="800" fill="#fff">S</text><circle cx="62" cy="26" r="8" fill="#8b5cf6"/><text x="62" y="29" text-anchor="middle" font-size="8" font-weight="800" fill="#fff">1</text><circle cx="100" cy="46" r="8" fill="#8b5cf6"/><text x="100" y="49" text-anchor="middle" font-size="8" font-weight="800" fill="#fff">2</text><circle cx="134" cy="26" r="8" fill="#111"/><text x="134" y="29" text-anchor="middle" font-size="8" font-weight="800" fill="#fff">F</text></svg>`;

export interface PickerOptions {
  root: HTMLElement;
  center: { lat: number; lng: number };
  onCreated: (quest: QuestDetail) => void;
  onToast: (message: string) => void;
}

/** "What kind of quest?" — GPS Art Quest and Spot Quest. */
export function mountPicker(opts: PickerOptions): void {
  const shell = el("div", "qe");
  shell.append(questBar({ crumb: t("qbar.newQuest") }));
  const ws = questViewWorkspace();
  const card = el("div", "qe-guide qpick");
  card.append(
    el("p", "qe-kicker qe-kicker-muted", t("qpick.kicker")),
    el("h1", "qe-heading", t("qpick.title")),
    el("p", "qe-sub", t("qpick.sub")),
  );
  let busy = false;
  const startSpot = pickCard("spot", SPOT_ART, async (b) => {
    if (busy) return;
    busy = true;
    b.disabled = true;
    b.querySelector(".qpick-cta")!.textContent = t("qpick.creating");
    try {
      const quest = await createMission({
        quest_type: "spot_trace",
        center_lat: opts.center.lat,
        center_lng: opts.center.lng,
        route_settings_done: true,
      });
      opts.onCreated(quest);
    } catch (err) {
      busy = false;
      b.disabled = false;
      b.querySelector(".qpick-cta")!.textContent = t("qpick.start");
      opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
    }
  });
  card.append(startSpot);
  card.append(
    pickCard("route", ROUTE_ART, async (b) => {
      if (busy) return;
      busy = true;
      b.disabled = true;
      b.querySelector(".qpick-cta")!.textContent = t("qpick.creating");
      try {
        const quest = await createMission({
          quest_type: "route_trace",
          center_lat: opts.center.lat,
          center_lng: opts.center.lng,
        });
        opts.onCreated(quest);
      } catch (err) {
        busy = false;
        b.disabled = false;
        b.querySelector(".qpick-cta")!.textContent = t("qpick.start");
        opts.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
      }
    }),
  );
  ws.guide.append(card);
  shell.append(ws.root);
  opts.root.replaceChildren(shell);
}

function pickCard(
  kind: "spot" | "route",
  art: string,
  onPick: ((b: HTMLButtonElement) => void) | null,
): HTMLElement {
  const b = button("", `qpick-card qpick-${kind}`, () => onPick?.(b));
  b.disabled = onPick == null;
  b.dataset.kind = kind;
  const artEl = el("span", "qpick-art");
  artEl.innerHTML = art;
  const body = el("span", "qpick-body");
  const head = el("span", "qpick-head");
  head.append(
    el("span", "qpick-title", t(kind === "spot" ? "qpick.spot.title" : "qpick.route.title")),
    el("span", onPick ? "qpick-cta" : "qpick-cta qpick-soon", onPick ? t("qpick.start") : t("qpick.soon")),
  );
  body.append(
    head,
    el("span", "qpick-line", t(kind === "spot" ? "qpick.spot.line" : "qpick.route.line")),
    el("span", "qpick-detail", t(kind === "spot" ? "qpick.spot.detail" : "qpick.route.detail")),
  );
  b.append(artEl, body);
  return b;
}
